import { useChatFileAttachment, UseChatFileAttachmentOptions } from './useChatFileAttachment';
import {
  ChatPromptInputState,
  useChatPromptInput,
  UseChatPromptInputOptions,
} from './useChatPromptInput';
import {
  useChatPromptSender,
  UseChatPromptSenderOptions,
  ChatPromptSenderState,
} from './useChatPromptSender';

export interface ChatPromptSendConfig
  extends Pick<UseChatPromptSenderOptions, 'buildPayload' | 'allowEmpty' | 'trimPrompt'> {
  onSend: UseChatPromptSenderOptions['onSend'];
  resetPromptOnSend?: boolean;
  clearAttachmentOnSend?: boolean;
  shouldPreventSend?: UseChatPromptSenderOptions['shouldPreventSend'];
}

export interface ChatPromptAttachmentConfig extends UseChatFileAttachmentOptions {
  clearOnPromptChange?: boolean;
}

export interface UseChatPromptOptions extends Pick<UseChatPromptInputOptions, 'initialPrompt'> {
  inputOptions?: Omit<UseChatPromptInputOptions, 'initialPrompt'>;
  sendConfig: ChatPromptSendConfig;
  attachmentConfig?: ChatPromptAttachmentConfig;
}

export interface ChatPromptState
  extends Pick<ChatPromptInputState, 'prompt' | 'setPrompt' | 'onPromptChange' | 'resetPrompt'>,
    Pick<ChatPromptSenderState, 'isSending' | 'sendPrompt'> {
  selectedFile: File | null;
  onFileSelected: (file: File | null) => void;
  clearFile: () => void;
  attachmentLabel?: string;
}

export const useChatPrompt = ({
  initialPrompt,
  inputOptions,
  sendConfig,
  attachmentConfig,
}: UseChatPromptOptions): ChatPromptState => {
  const { clearOnPromptChange = true, ...restAttachmentConfig } = attachmentConfig ?? {};

  const attachmentOptions: UseChatFileAttachmentOptions = {
    ...restAttachmentConfig,
    enabled: attachmentConfig ? attachmentConfig.enabled !== false : false,
  };

  const attachmentState = useChatFileAttachment(attachmentOptions);

  const promptInput = useChatPromptInput({
    initialPrompt,
    onBeforeChange:
      attachmentOptions.enabled && clearOnPromptChange
        ? () => attachmentState.clearFile()
        : undefined,
    ...(inputOptions ?? {}),
  });

  const { sendPrompt, isSending } = useChatPromptSender({
    getPrompt: () => promptInput.prompt,
    getRawPrompt: () => promptInput.prompt,
    getAttachment: () => attachmentState.selectedFile,
    onSend: sendConfig.onSend,
    buildPayload: sendConfig.buildPayload,
    allowEmpty: sendConfig.allowEmpty,
    trimPrompt: sendConfig.trimPrompt,
    resetPrompt: promptInput.resetPrompt,
    clearAttachment: attachmentState.clearFile,
    resetPromptOnSend: sendConfig.resetPromptOnSend,
    clearAttachmentOnSend: sendConfig.clearAttachmentOnSend,
    shouldPreventSend: sendConfig.shouldPreventSend,
  });

  return {
    prompt: promptInput.prompt,
    setPrompt: promptInput.setPrompt,
    onPromptChange: promptInput.onPromptChange,
    resetPrompt: promptInput.resetPrompt,
    isSending,
    sendPrompt,
    selectedFile: attachmentState.selectedFile,
    onFileSelected: attachmentState.selectFile,
    clearFile: attachmentState.clearFile,
    attachmentLabel: attachmentState.attachmentLabel,
  };
};
